import React, {createRef, ReactNode, RefObject} from "react";
import PortalDocument, {PortalDocumentInterface} from "../document/PortalDocument";
import DocumentSettings from "../document/DocumentSettings";
import {PortalGlobals} from "../Portal";
import LeftSidebar from "./sidebar/LeftSidebar";
import RightSidebar from "./sidebar/RightSidebar";
import LeftSidebarButtons from "./sidebar/LeftSidebarButtons";
import RightSidebarButtons from "./sidebar/RightSidebarButtons";
import {PortalDocumentManagerGlobals} from "../document/PortalDocumentManager";
import {I18nGlobals} from "../../shared/translation/i18nInterface";
import DefaultViewer from "./DefaultViewer";
import PdfViewer, {PdfViewerDefinition} from "./pdf/PdfViewer";
import {ViewerMode} from "./ViewerMode";
import ImageViewer, {ImageViewerDefinition} from "./image/ImageViewer";
import ViewerType from "../document/DataType";
import HeicViewer, {HeicViewerDefinition} from "./image/HeicViewer";
import TiffViewer, {TiffViewerDefinition} from "./image/TiffViewer";
import HtmlViewer, {HtmlViewerDefinition} from "./html/HtmlViewer";
import RtfViewer, {RtfViewerDefinition} from "./rtf/RtfViewer";
import ZipViewer, {ZipViewerDefinition} from "./zip/ZipViewer";
import TextViewer, {TextViewerDefinition} from "./text/TextViewer";
import Observer from "../../shared/class/Observer";
import LocationEvent from "./LocationEvent";
import DestinationEvent from "./DestinationEvent";
import {TextSearchGlobals} from "../document/components/pdf/TextSearch";
import PortfolioList from "./pdf/PortfolioList";
import Immutable from "../../shared/immutable/Immutable";
import {Attachment} from "@softvision/webpdf-wsclient-9-typescript/lib/generated-sources";
import MimeType from "../document/mimetype/MimeType";
import MimeTypeFilter from "../document/mimetype/MimeTypeFilter";
import MimeTypes from "../document/mimetype/MimeTypes";
import Translate from "../../shared/translation/Translate";
import {AreaLayerGlobals} from "./pdf/AreaLayerInterface";
import {SidebarFunctionGlobals} from "../document/components/pdf/SidebarFunctions";
import StorageItem from "../../shared/StorageItem";
import FunctionHelper from "../../shared/helper/FunctionHelper";
import {DebugGlobals} from "../dialog/components/settings/DebugDialog";
import {FormLayerGlobals} from "./pdf/form/FormLayerInterface";
import {HelperGlobals} from "../../shared/components/Helper";
import ViewerToolbar from "./toolbar/ViewerToolbar";
import LoadingViewer from "./LoadingViewer";
import PortalPureComponent from "../../shared/components/PortalPureComponent";

export interface ViewerScreenProperties extends PortalDocumentManagerGlobals, PortalGlobals, I18nGlobals,
	TextSearchGlobals, AreaLayerGlobals, SidebarFunctionGlobals, DebugGlobals, FormLayerGlobals, HelperGlobals {
	portalDocument?: Immutable<PortalDocumentInterface>;
}

export interface ViewerScreenState {
	fullscreen: boolean;
}

export class ViewerScreenDefinition extends PortalPureComponent<ViewerScreenProperties, ViewerScreenState> {
	private viewerScreen: RefObject<HTMLDivElement>;
	private locationObserver: Observer<LocationEvent>;
	private destinationObserver: Observer<DestinationEvent>;

	/**
	 * Viewer screen component constructor
	 */
	constructor(props: Readonly<ViewerScreenProperties>) {
		super(props);

		this.locationObserver = new Observer();
		this.destinationObserver = new Observer();
		this.viewerScreen = createRef<HTMLDivElement>();

		this.state = {
			fullscreen: false
		};

		this.toggleFullscreen = this.toggleFullscreen.bind(this);
	}

	/**
	 * This is called when the component is mounted
	 */
	public async componentDidMount(): Promise<void> {
		await super.componentDidMount();

		window.addEventListener('fullscreenchange', this.toggleFullscreen); /* default */
		window.addEventListener('mozfullscreenchange', this.toggleFullscreen); /* Firefox */
		window.addEventListener('webkitfullscreenchange', this.toggleFullscreen); /* Chrome, Safari and Opera */
		window.addEventListener('msfullscreenchange', this.toggleFullscreen); /* IE / Edge */
	}

	/**
	 * This is called when the component is unmounted
	 */
	public async componentWillUnmount(): Promise<void> {
		await super.componentWillUnmount();

		this.locationObserver.disconnect();
		this.destinationObserver.disconnect();

		window.removeEventListener('fullscreenchange', this.toggleFullscreen); /* default */
		window.removeEventListener('mozfullscreenchange', this.toggleFullscreen); /* Firefox */
		window.removeEventListener('webkitfullscreenchange', this.toggleFullscreen); /* Chrome, Safari and Opera */
		window.removeEventListener('msfullscreenchange', this.toggleFullscreen); /* IE / Edge */
	}

	/**
	 * This is called when the components props or state is updated
	 */
	public async componentDidUpdate(
		prevProps: Readonly<ViewerScreenProperties>, prevState: Readonly<any>, snapshot?: any
	): Promise<void> {
		if (!FunctionHelper.exists(this.props.portalDocument?.value)) {
			return;
		}

		let portalDocument: PortalDocument = this.props.context.documentManager.findDocument(
			this.props.portalDocument!.get().id!
		);
		let viewerSettings: DocumentSettings = portalDocument.getViewerSettings();

		if (viewerSettings.fullscreen) {
			viewerSettings.fullscreen = false;
			await this.requestFullscreen();
		}
	}

	/**
	 * request fullscreen mode for this viewer screen
	 */
	private async requestFullscreen(): Promise<void> {
		if (!FunctionHelper.exists(this.props.portalDocument?.value) || navigator.userAgent.indexOf('Edge') > -1 ||
			this.viewerScreen.current === null) {
			// Browser ist ein edge (fullscreen nur per direkt klick)
			return;
		}

		let applyFullscreen: () => Promise<void> = async () => {
			try {
				await this.viewerScreen.current!.requestFullscreen();
			} catch (e: any) {
				// can be ignored
			}
		};

		if (localStorage.getItem(StorageItem.FULLSCREEN_ALLOWED) !== "true") {
			this.props.context.getPage().setState({
				showConfirmDialog: true,
				confirmDialogMessage: Translate.value("viewerscreen.message.fullscreen", {ns: "screen"}),
				confirmDialogSubmit: async () => {
					localStorage.setItem(StorageItem.FULLSCREEN_ALLOWED, String(true));
					await applyFullscreen();
				}
			});
			return;
		}

		await applyFullscreen();
	}

	private toggleFullscreen() {
		let fullscreen: boolean = document.fullscreenElement !== null;

		if (!FunctionHelper.exists(this.props.portalDocument?.value)) {
			return;
		}

		let portalDocument: PortalDocument = this.props.context.documentManager.findDocument(
			this.props.portalDocument!.get().id!
		);
		let viewerSettings: DocumentSettings = portalDocument.getViewerSettings();
		viewerSettings.fullscreen = fullscreen;
		this.props.context.documentManager.updateGlobalDocumentList({}, () => {
			this.setState({
				fullscreen: fullscreen
			});
		});
	}

	private renderPortfolioFile(): ReactNode {
		if (!FunctionHelper.exists(this.props.portalDocument?.value)) {
			return null;
		}

		let portalDocument: PortalDocument = this.props.context.documentManager.findDocument(
			this.props.portalDocument!.get().id!
		);
		let documentSettings: DocumentSettings = portalDocument.getSettings();
		let viewerSettings: DocumentSettings = portalDocument.getViewerSettings();
		let isFolderSelected: boolean = FunctionHelper.exists(portalDocument.getFolderByPath(
			FunctionHelper.applyValue(documentSettings.portfolioSelection, "")
		));

		let selectedAttachment: Attachment | undefined = portalDocument.getAttachment(
			!isFolderSelected ? FunctionHelper.applyValue(documentSettings.portfolioSelection, "") : ""
		);

		if (portalDocument.isLocked()) {
			return <LoadingViewer {...this.props} viewerMode={ViewerMode.PORTFOLIO}/>;
		}

		if (!FunctionHelper.exists(selectedAttachment)) {
			return null;
		}

		let mimeType: MimeType = portalDocument.getMimeType(selectedAttachment);
		let viewerType: ViewerType = portalDocument.getViewerType(selectedAttachment);

		if (MimeTypeFilter.checkSelection([mimeType], [MimeTypes.DOC, MimeTypes.DOCX])) {
			viewerType = ViewerType.DEFAULT;
		}

		switch (viewerType) {
			case ViewerType.PDF:
				return <PdfViewer {...PdfViewerDefinition.defaults()} {...this.props} {...this.state}
								  currentPage={viewerSettings.activeViewer.currentPage}
								  viewerMode={ViewerMode.PORTFOLIO}
								  pagePadding={viewerSettings.activeViewer.pagePadding}
								  pageCover={viewerSettings.activeViewer.pageCover}
								  zoomMode={viewerSettings.activeViewer.zoomMode}
								  mouseMode={viewerSettings.activeViewer.mouseMode}
								  pageMode={viewerSettings.activeViewer.pageMode}
								  continuous={viewerSettings.activeViewer.continuous}
								  zoomValue={viewerSettings.activeViewer.zoomValue}
								  locationObserver={this.locationObserver}
								  attachmentKey={selectedAttachment!.objectKey}
								  mouseFunction={viewerSettings.activeViewer.mouseFunction}
								  destinationObserver={this.destinationObserver}/>;
			case ViewerType.IMAGE:
				return <ImageViewer {...ImageViewerDefinition.defaults()} {...this.props} {...this.state}
									attachmentKey={selectedAttachment!.objectKey} viewerMode={ViewerMode.PORTFOLIO}
									zoomMode={viewerSettings.activeViewer.zoomMode}
									mouseMode={viewerSettings.activeViewer.mouseMode}
									zoomValue={viewerSettings.activeViewer.zoomValue}/>;
			case ViewerType.HEIC:
				return <HeicViewer {...HeicViewerDefinition.defaults()} {...this.props} {...this.state}
								   attachmentKey={selectedAttachment!.objectKey} viewerMode={ViewerMode.PORTFOLIO}
								   currentPage={viewerSettings.activeViewer.currentPage}
								   zoomValue={viewerSettings.activeViewer.zoomValue}
								   zoomMode={viewerSettings.activeViewer.zoomMode}
								   mouseMode={viewerSettings.activeViewer.mouseMode}/>;
			case ViewerType.TIFF:
				return <TiffViewer {...TiffViewerDefinition.defaults()} {...this.props} {...this.state}
								   attachmentKey={selectedAttachment!.objectKey} viewerMode={ViewerMode.PORTFOLIO}
								   currentPage={viewerSettings.activeViewer.currentPage}
								   zoomValue={viewerSettings.activeViewer.zoomValue}
								   zoomMode={viewerSettings.activeViewer.zoomMode}
								   mouseMode={viewerSettings.activeViewer.mouseMode}/>;
			case ViewerType.TEXT:
				return <TextViewer {...TextViewerDefinition.defaults()} {...this.props} {...this.state}
								   attachmentKey={selectedAttachment!.objectKey}
								   mouseMode={viewerSettings.activeViewer.mouseMode}
								   viewerMode={ViewerMode.PORTFOLIO} wrapText={viewerSettings.textWrap}
								   viewAsMarkdown={viewerSettings.viewAsMarkdown}/>;
			case ViewerType.ZIP:
				return <ZipViewer {...ZipViewerDefinition.defaults()} {...this.props} {...this.state}
								  attachmentKey={selectedAttachment!.objectKey}
								  mouseMode={viewerSettings.activeViewer.mouseMode}
								  viewerMode={ViewerMode.PORTFOLIO}/>;
			case ViewerType.HTML:
				return <HtmlViewer {...HtmlViewerDefinition.defaults()} {...this.props} {...this.state}
								   attachmentKey={selectedAttachment!.objectKey} wrapText={viewerSettings.textWrap}
								   mouseMode={viewerSettings.activeViewer.mouseMode}
								   viewerMode={ViewerMode.PORTFOLIO} viewAsHTML={viewerSettings.viewAsHTML}/>;
			case ViewerType.RTF:
				return <RtfViewer {...RtfViewerDefinition.defaults()} {...this.props} {...this.state}
								  attachmentKey={selectedAttachment!.objectKey}
								  mouseMode={viewerSettings.activeViewer.mouseMode}
								  viewerMode={ViewerMode.PORTFOLIO}/>;
		}

		return <DefaultViewer {...this.props} {...this.state} attachmentKey={selectedAttachment!.objectKey}
							  portalDocument={this.props.portalDocument!} viewerMode={ViewerMode.PORTFOLIO}
							  mouseMode={viewerSettings.activeViewer.mouseMode}/>;
	}

	private renderViewer(): ReactNode {
		if (!FunctionHelper.exists(this.props.portalDocument?.value)) {
			return null;
		}

		let portalDocument: PortalDocument = this.props.context.documentManager.findDocument(
			this.props.portalDocument!.get().id!
		);
		let documentSettings: DocumentSettings = portalDocument.getSettings();

		if (portalDocument.isPortfolio() && !FunctionHelper.isEmpty(documentSettings.portfolioSelection)) {
			return this.renderPortfolioFile();
		}

		if (portalDocument.isLocked()) {
			return <LoadingViewer {...this.props} viewerMode={ViewerMode.DEFAULT}/>;
		}

		switch (portalDocument.getViewerType()) {
			case ViewerType.PDF:
				return <PdfViewer {...PdfViewerDefinition.defaults()} {...this.props} {...this.state}
								  portalDocument={this.props.portalDocument!}
								  currentPage={documentSettings.activeViewer.currentPage}
								  pagePadding={documentSettings.activeViewer.pagePadding}
								  pageCover={documentSettings.activeViewer.pageCover}
								  zoomMode={documentSettings.activeViewer.zoomMode}
								  mouseMode={documentSettings.activeViewer.mouseMode}
								  pageMode={documentSettings.activeViewer.pageMode}
								  continuous={documentSettings.activeViewer.continuous}
								  zoomValue={documentSettings.activeViewer.zoomValue}
								  mouseFunction={documentSettings.activeViewer.mouseFunction}
								  locationObserver={this.locationObserver}
								  destinationObserver={this.destinationObserver}/>;
			case ViewerType.IMAGE:
				return <ImageViewer {...ImageViewerDefinition.defaults()} {...this.props} {...this.state}
									portalDocument={this.props.portalDocument!}
									zoomMode={documentSettings.activeViewer.zoomMode}
									mouseMode={documentSettings.activeViewer.mouseMode}
									zoomValue={documentSettings.activeViewer.zoomValue}/>;
			case ViewerType.HEIC:
				return <HeicViewer {...HeicViewerDefinition.defaults()} {...this.props} {...this.state}
								   portalDocument={this.props.portalDocument!}
								   currentPage={documentSettings.activeViewer.currentPage}
								   zoomValue={documentSettings.activeViewer.zoomValue}
								   zoomMode={documentSettings.activeViewer.zoomMode}
								   mouseMode={documentSettings.activeViewer.mouseMode}/>;
			case ViewerType.TIFF:
				return <TiffViewer {...TiffViewerDefinition.defaults()} {...this.props} {...this.state}
								   portalDocument={this.props.portalDocument!}
								   currentPage={documentSettings.activeViewer.currentPage}
								   zoomValue={documentSettings.activeViewer.zoomValue}
								   zoomMode={documentSettings.activeViewer.zoomMode}
								   mouseMode={documentSettings.activeViewer.mouseMode}/>;
			case ViewerType.HTML:
				return <HtmlViewer {...HtmlViewerDefinition.defaults()} {...this.props} {...this.state}
								   portalDocument={this.props.portalDocument!} wrapText={documentSettings.textWrap}
								   mouseMode={documentSettings.activeViewer.mouseMode}
								   viewAsHTML={documentSettings.viewAsHTML}/>;
			case ViewerType.RTF:
				return <RtfViewer {...RtfViewerDefinition.defaults()} {...this.props} {...this.state}
								  portalDocument={this.props.portalDocument!}
								  mouseMode={documentSettings.activeViewer.mouseMode}/>;
			case ViewerType.ZIP:
				return <ZipViewer {...ZipViewerDefinition.defaults()} {...this.props} {...this.state}
								  portalDocument={this.props.portalDocument!}
								  mouseMode={documentSettings.activeViewer.mouseMode}/>;
			case ViewerType.TEXT:
				return <TextViewer {...TextViewerDefinition.defaults()} {...this.props} {...this.state}
								   portalDocument={this.props.portalDocument!} wrapText={documentSettings.textWrap}
								   mouseMode={documentSettings.activeViewer.mouseMode}
								   viewAsMarkdown={documentSettings.viewAsMarkdown}/>;
		}

		return <DefaultViewer {...this.props} {...this.state} portalDocument={this.props.portalDocument!}
							  viewerMode={ViewerMode.DEFAULT}
							  mouseMode={documentSettings.activeViewer.mouseMode}/>;
	}

	/**
	 * render component as react node
	 */
	public render(): ReactNode {
		if (!FunctionHelper.exists(this.props.portalDocument?.value)) {
			return null;
		}

		let classList: Array<string> = ["viewer-screen"];

		if (this.state.fullscreen) {
			classList.push("fullscreen");
		}

		return (
			<>
				<div className={classList.join(" ")} ref={this.viewerScreen}>
					<PortfolioList {...this.props} portalDocument={this.props.portalDocument!}
								   viewerScreen={this.viewerScreen}/>
					<div className="toolbar vertical" data-help="global.sidebar.left" data-position="right">
						<LeftSidebarButtons {...this.props}/>
					</div>
					<div className="content-panel">
						<LeftSidebar {...this.props} portalDocument={this.props.portalDocument!}
									 viewerScreen={this.viewerScreen} locationObserver={this.locationObserver}
									 destinationObserver={this.destinationObserver}/>
						<RightSidebar {...this.props} viewerScreen={this.viewerScreen}
									  locationObserver={this.locationObserver}/>
						<ViewerToolbar {...this.props}/>
						<div className="viewer-container" data-help="global.screen.viewer" data-position="top">
							{this.renderViewer()}
						</div>
					</div>
					<div className="toolbar vertical inverted" data-help="global.sidebar.right"
						 data-position="left">
						<RightSidebarButtons {...this.props}/>
					</div>
				</div>
			</>
		);
	}
}

const ViewerScreen = React.memo(ViewerScreenDefinition,
	(prevProps: Readonly<ViewerScreenProperties>, nextProps: Readonly<ViewerScreenProperties>) => {
		if (FunctionHelper.exists(nextProps.portalDocument) &&
			!nextProps.portalDocument!.equals(prevProps.portalDocument)) {
			return false;
		}

		if (FunctionHelper.exists(nextProps.textSearchResults) &&
			!nextProps.textSearchResults!.equals(prevProps.textSearchResults)) {
			return false;
		}

		if (FunctionHelper.exists(nextProps.userSettings) &&
			!nextProps.userSettings.equals(prevProps.userSettings)) {
			return false;
		}

		if (!nextProps.userCertificates.equals(prevProps.userCertificates)) {
			return false;
		}

		if (nextProps.textSearchSelection !== prevProps.textSearchSelection) {
			return false;
		}

		if (nextProps.language !== prevProps.language) {
			return false;
		}

		if (!prevProps.mouseFunctionAnnotationParameter?.equals(nextProps.mouseFunctionAnnotationParameter)) {
			return false;
		}

		if (nextProps.showAnnotationLineFunction !== prevProps.showAnnotationLineFunction) {
			return false;
		}

		if (nextProps.showAnnotationCircleFunction !== prevProps.showAnnotationCircleFunction) {
			return false;
		}

		if (nextProps.showAnnotationSquareFunction !== prevProps.showAnnotationSquareFunction) {
			return false;
		}

		if (nextProps.showAnnotationMarkFunction !== prevProps.showAnnotationMarkFunction) {
			return false;
		}

		if (nextProps.showAnnotationUnderlineFunction !== prevProps.showAnnotationUnderlineFunction) {
			return false;
		}

		if (nextProps.showAnnotationSquigglyFunction !== prevProps.showAnnotationSquigglyFunction) {
			return false;
		}

		if (nextProps.showAnnotationStrikeFunction !== prevProps.showAnnotationStrikeFunction) {
			return false;
		}

		if (nextProps.showAnnotationNoteFunction !== prevProps.showAnnotationNoteFunction) {
			return false;
		}

		if (nextProps.showRedactAreaFunction !== prevProps.showRedactAreaFunction) {
			return false;
		}

		if (nextProps.showRedactTextFunction !== prevProps.showRedactTextFunction) {
			return false;
		}

		if (nextProps.showAnnotationReplyFunction !== prevProps.showAnnotationReplyFunction) {
			return false;
		}

		if (nextProps.showAnnotationStampFunction !== prevProps.showAnnotationStampFunction) {
			return false;
		}

		if (nextProps.showFormsEditFunction !== prevProps.showFormsEditFunction) {
			return false;
		}

		if (nextProps.portalResolution !== prevProps.portalResolution) {
			return false;
		}

		if (nextProps.updateSidebarFunction !== prevProps.updateSidebarFunction) {
			return false;
		}

		if (FunctionHelper.exists(nextProps.mouseFunctionPoint) &&
			!nextProps.mouseFunctionPoint!.equals(prevProps.mouseFunctionPoint)) {
			return false;
		}

		if (FunctionHelper.exists(nextProps.mouseFunctionRect) &&
			!nextProps.mouseFunctionRect!.equals(prevProps.mouseFunctionRect)) {
			return false;
		}

		if (FunctionHelper.exists(nextProps.mouseFunctionLine) &&
			!nextProps.mouseFunctionLine!.equals(prevProps.mouseFunctionLine)) {
			return false;
		}

		if (FunctionHelper.exists(nextProps.formValues) && !nextProps.formValues!.equals(prevProps.formValues)) {
			return false;
		}

		if (FunctionHelper.exists(nextProps.dragEvent) && !nextProps.dragEvent!.equals(prevProps.dragEvent)) {
			return false;
		}

		return true;
	}
);

export default ViewerScreen;